import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, ScanLine, Copy, RefreshCw, Download, Nfc } from "lucide-react";

export default function AttendancePage() {
  const { user } = useAuth();
  const { query } = usePlanningPeriod();
  const isAdmin = user?.role === "admin";
  const [config, setConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const checkinUrl = config?.token ? `${window.location.origin}/checkin?token=${config.token}` : "";

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/attendance/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/attendance?${query}`)
      .then((r) => { if (active) setRecords(r.data); })
      .catch((e) => { if (active) toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query]);

  const toggleModule = async (enabled) => {
    setBusy(true);
    try {
      await api.post("/attendance/config", { enabled });
      setConfig((c) => ({ ...c, enabled }));
      toast.success(enabled ? "Inchecken ingeschakeld" : "Inchecken uitgeschakeld");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/config/regenerate");
      setConfig((c) => ({ ...c, ...data }));
      toast.success("Nieuwe tag-link gegenereerd. Herschrijf je NFC-tag.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(checkinUrl);
    toast.success("Link gekopieerd");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/attendance/export?${query}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aanwezigheid.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <ScanLine className="w-7 h-7 text-red-600" /> Aanwezigheid
          </h1>
          <p className="text-muted-foreground mt-1">Wie heeft wanneer ingecheckt via de NFC-tag.</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="attendance-export"><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
      </div>

      {isAdmin && (
        <Card className="p-6" data-testid="attendance-config">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0">
                <Nfc className="w-5 h-5 text-red-700 dark:text-red-300" />
              </div>
              <div>
                <h3 className="font-heading font-semibold">NFC-inchecken module</h3>
                <p className="text-sm text-muted-foreground max-w-md mt-0.5">
                  Zet de module aan en schrijf de onderstaande link op een NFC-tag. Personeel tikt de tag aan om in te checken.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{config?.enabled ? "Aan" : "Uit"}</span>
              <Switch checked={!!config?.enabled} disabled={busy || !config} onCheckedChange={toggleModule} data-testid="attendance-module-toggle" />
            </div>
          </div>

          {config?.enabled && (
            <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-6 items-center border-t pt-6">
              <div className="space-y-3 min-w-0">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Tag-link</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 break-all flex-1" data-testid="attendance-url">{checkinUrl}</code>
                    <Button variant="outline" size="icon" onClick={copyUrl} aria-label="Kopieer link" data-testid="attendance-copy"><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={regenerate} disabled={busy} className="text-amber-700 hover:text-amber-800 hover:bg-amber-50" data-testid="attendance-regenerate">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Nieuwe link genereren
                </Button>
                <p className="text-xs text-muted-foreground">Schrijf deze link met een gratis NFC-app (bijv. "NFC Tools") op een NTAG213/215-tag. De QR-code werkt als back-up voor telefoons zonder NFC.</p>
              </div>
              <div className="flex flex-col items-center gap-2 justify-self-center">
                <div className="bg-white p-3 rounded-xl border" data-testid="attendance-qr">
                  <QRCodeSVG value={checkinUrl} size={150} />
                </div>
                <p className="text-xs text-muted-foreground">QR back-up</p>
              </div>
            </div>
          )}
        </Card>
      )}

      <PlanningPeriodSelector />

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : records.length === 0 ? (
          <div className="text-center text-muted-foreground py-16" data-testid="attendance-empty">Nog geen inchecks in deze periode.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Tijd</TableHead>
                <TableHead>Naam</TableHead>
                <TableHead>Dienst</TableHead>
                <TableHead>Ingepland</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id} data-testid={`attendance-row-${r.id}`}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell className="font-semibold">{r.local_time}</TableCell>
                  <TableCell className="font-medium">{r.user_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.service_name ? `${r.service_name} · ${r.service_time}` : "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${r.scheduled ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                      {r.scheduled ? "Ja" : "Niet ingepland"}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
